"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8">
          <div className="h-12 w-3/4 bg-muted/30 rounded-lg animate-pulse" />
          <div className="h-96 w-full bg-muted/30 rounded-xl animate-pulse" />
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} data-index={i} className="h-4 bg-muted/30 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Failed to load post content.</p>
          <Link to="/ResourcesPage">
            <Button variant="outline">
              <ArrowLeft className="size-4" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Post not found.</p>
          <Link to="/ResourcesPage">
            <Button variant="outline">
              <ArrowLeft className="size-4" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-12 max-w-4xl">
      <div className="mb-6">
        <Link to="/ResourcesPage">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="size-4" />
            Back to Resources
          </Button>
        </Link>
      </div>

      <header className="mb-8 space-y-6">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time>{new Date(post.date).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}</time>
            </div>
          )}

          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                {post.author.avatar?.["48"] && (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                )}
                <AvatarFallback>{post.author.name.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span>{post.author.name}</span>
            </div>
          )}

          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="size-4" />
              <div className="flex gap-2">
                {post.categories.map((cat, i) => (
                  <span key={cat.id} data-index={i}>
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-8 rounded-xl overflow-hidden shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[500px]"
          />
        </div>
      )}

      <Separator className="my-8" />

      <div
        className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary hover:prose-a:text-primary-hover prose-strong:text-foreground prose-ul:text-foreground prose-ol:text-foreground"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-8" />

      {post.author && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>About the Author</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <Avatar className="size-16">
                {post.author.avatar?.["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="text-lg">{post.author.name.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg mb-1">{post.author.name}</h3>
                <p className="text-muted-foreground text-sm">
                  Contributing author at Education Family Tutors
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium text-muted-foreground">Tags:</span>
          {post.tags.map((tag, i) => (
            <span
              key={tag.id}
              data-index={i}
              className="inline-flex items-center px-3 py-1 rounded-md bg-accent/10 text-accent text-sm"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function RelatedPosts() {
  const { posts: currentPosts } = usePosts();
  const currentPost = currentPosts[0];

  if (!currentPost) return null;

  const categoryIds = currentPost.categories?.map(cat => cat.id) || [];

  if (categoryIds.length === 0) return null;

  return (
    <section className="bg-muted-light-background py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl font-display font-bold text-foreground mb-8">Related Posts</h2>
        <PostsProvider className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" postType="post" wp_query={{
            per_page: 3,
            exclude: [currentPost.id],
            categories: categoryIds,
            orderby: "date",
            order: "desc"
          }} embeds={["wp:featuredmedia", "wp:term"]} fields={["id", "title.rendered", "excerpt.rendered", "link", "date", "_embedded", "_links.wp:featuredmedia", "_links.wp:term"]}>
          <RelatedPostsGrid />
        </PostsProvider>
      </div>
    </section>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <>
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="h-80 animate-pulse bg-muted/30" />
        ))}
      </>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <>
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="hover-lift group">
          {post.featuredImage && (
            <div className="overflow-hidden rounded-t-xl">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover transition-transform duration-[var(--duration-medium)] group-hover:scale-105"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors duration-[var(--duration-fast)]">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {post.excerpt && (
              <div
                className="text-sm text-muted-foreground line-clamp-3"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            )}
            {post.date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="size-3" />
                <time>{new Date(post.date).toLocaleDateString("en-GB")}</time>
              </div>
            )}
            {post.link && (
              <Link to={post.link}>
                <Button variant="outline" size="sm" className="w-full">
                  Read More
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = typeof window !== 'undefined' && (window as any).wvcClient ? (window as any).wvcClient.getCurrentPostID() : null;

  if (!currentPostId) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl text-center">
        <p className="text-muted-foreground mb-4">Unable to load post.</p>
        <Link to="/ResourcesPage">
          <Button variant="outline">
            <ArrowLeft className="size-4" />
            Back to Resources
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:term"]} fields={["id", "categories", "_links.wp:term"]}>
        <RelatedPosts />
      </PostsProvider>
    </>
  );
}